/**
 * Tiny Plausible Analytics helper.
 *
 * Usage:
 *   import { track } from "@/lib/analytics";
 *   track("Register Success");
 *
 * Safe to call before the Plausible script has loaded — the stub in
 * `public/index.html` queues calls and replays them once the real script
 * is ready.
 */
export const track = (eventName, props) => {
  if (typeof window === "undefined") return;
  if (typeof window.plausible !== "function") return;
  try {
    if (props) {
      window.plausible(eventName, { props });
    } else {
      window.plausible(eventName);
    }
  } catch {
    // analytics must never break the app
  }
};

/**
 * Fire an event once per browser per user — used for one-shot activation
 * metrics like "First Report Created". Uses localStorage so re-firing
 * within the same browser session is suppressed.
 *
 *   trackOnce("First Report Created", "user_abc123", { type: "quick" });
 *
 * The (eventName + scopeKey) combination is the dedup key. Pass a stable
 * user identifier so the event fires once per user per browser, regardless
 * of how many sessions they have.
 */
export const trackOnce = (eventName, scopeKey, props) => {
  if (typeof window === "undefined") return;
  try {
    const flagKey = `analytics:once:${eventName}:${scopeKey || "anon"}`;
    if (window.localStorage.getItem(flagKey)) return;
    track(eventName, props);
    window.localStorage.setItem(flagKey, "1");
  } catch {
    // localStorage unavailable (private mode etc.) — fall back to a normal track
    track(eventName, props);
  }
};


/**
 * Fire a Google Ads conversion event.
 *
 * Safe to call before gtag.js has loaded — gtag is dropped silently in
 * dev/preview/SSR environments. Production index.html loads gtag.js so
 * this fires reliably there.
 *
 * Usage:
 *   import { trackAdConversion } from "@/lib/analytics";
 *   trackAdConversion("AW-18165868123/L5kHCMb7nLEcENvM1NzD", { value: 1.0 });
 *
 * Pair with `trackAdConversionOnce` when an event must fire only once per
 * browser (e.g. signup) to avoid double-counting on re-renders or refreshes.
 */
export const trackAdConversion = (sendTo, { value = 1.0, currency = "EUR" } = {}) => {
  if (typeof window === "undefined") return;
  if (typeof window.gtag !== "function") return;
  try {
    window.gtag("event", "conversion", {
      send_to: sendTo,
      value,
      currency,
    });
  } catch {
    // analytics must never break the app
  }
};

/**
 * Same as trackAdConversion, but de-duped per (sendTo + scopeKey) using
 * localStorage. Use for one-shot conversions like "Sign up" — guarantees
 * each user is reported at most once even if Register navigates back here.
 */
export const trackAdConversionOnce = (sendTo, scopeKey, options) => {
  if (typeof window === "undefined") return;
  try {
    const flagKey = `analytics:adconv:${sendTo}:${scopeKey || "anon"}`;
    if (window.localStorage.getItem(flagKey)) return;
    trackAdConversion(sendTo, options);
    window.localStorage.setItem(flagKey, "1");
  } catch {
    trackAdConversion(sendTo, options);
  }
};

/**
 * Sign-up attribution helpers — captures where the user came from so we can
 * attribute conversions in Plausible later.
 *
 * Strategy:
 *   - On the first landing/page visit we read `utm_source`, `utm_medium`,
 *     `utm_campaign`, `gclid` (Google Ads click ID), and `document.referrer`.
 *   - We store them in localStorage. We never overwrite a previously captured
 *     source unless a fresh attribution param arrives (gclid or utm_source) —
 *     this matches first-touch attribution which is what we usually want for
 *     B2B SaaS where decision cycles are days, not seconds.
 *   - When Register fires, we read the source bundle and attach it to the
 *     Plausible event as custom props.
 */
const SOURCE_KEY = "veraso:source";

const _readQueryParam = (name) => {
  if (typeof window === "undefined") return null;
  try {
    return new URLSearchParams(window.location.search).get(name);
  } catch { return null; }
};

const _bucketReferrer = (ref) => {
  // Group raw referrers into useful buckets for analytics.
  if (!ref) return null;
  try {
    const host = new URL(ref).hostname.toLowerCase();
    if (host === window.location.hostname) return null; // internal
    if (host.includes("google.")) return "google-organic";
    if (host.includes("bing.com") || host.includes("duckduckgo.com") || host.includes("yahoo.")) return "search-other";
    if (host.includes("linkedin.com")) return "linkedin";
    if (host.includes("facebook.com") || host.includes("instagram.com")) return "meta";
    if (host.includes("reddit.com")) return "reddit";
    if (host.includes("youtube.com")) return "youtube";
    return host.replace(/^www\./, "");
  } catch { return null; }
};

/**
 * Call this on the landing page (`Landing.jsx` / `LandingEN.jsx` /
 * `NicheLanding.jsx`) load. Idempotent — only writes when there is fresh
 * attribution data to capture.
 */
export const captureSignupSource = () => {
  if (typeof window === "undefined") return;
  try {
    const gclid = _readQueryParam("gclid");
    const utm_source = _readQueryParam("utm_source");
    const utm_medium = _readQueryParam("utm_medium");
    const utm_campaign = _readQueryParam("utm_campaign");
    const referrerBucket = _bucketReferrer(document.referrer);

    const existing = JSON.parse(window.localStorage.getItem(SOURCE_KEY) || "null");
    const hasFreshAttribution = !!(gclid || utm_source);
    if (existing && !hasFreshAttribution) return; // keep first-touch

    const bundle = {
      source: utm_source || (gclid ? "google-ads" : referrerBucket || "direct"),
      medium: utm_medium || (gclid ? "cpc" : referrerBucket ? "referral" : "direct"),
      campaign: utm_campaign || null,
      gclid: gclid || null,
      landing_path: window.location.pathname,
      captured_at: new Date().toISOString(),
    };
    window.localStorage.setItem(SOURCE_KEY, JSON.stringify(bundle));
  } catch {
    /* never break the page */
  }
};

/**
 * Returns the previously-captured source bundle (or a "direct" fallback).
 * Used at Register success time to enrich the Plausible event.
 */
export const getSignupSource = () => {
  if (typeof window === "undefined") return { source: "direct", medium: "direct" };
  try {
    const v = JSON.parse(window.localStorage.getItem(SOURCE_KEY) || "null");
    if (v && typeof v === "object") return v;
  } catch { /* ignore */ }
  return { source: "direct", medium: "direct" };
};
